import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { fetchEmployes } from "../../store/actions/depannageActions"
import {
	fetchAtelierSearchV2,
	setAtelierSearchCriteres,
} from "../../store/actions/atelierSearchActions"
import { useGroups } from "../../hooks/groups"
import { generalStyles } from "../styles/styles"
import InputV2 from "../basics/input/InputV2"
import { COLORS } from "../../utilities/Constants"
import {
	ATELIER_ETATS_CHOICES,
	dateFrToISO,
	dateISOToFr,
} from "../../utilities/Addons"

// Ligne label + champ. ⚠️ Module-level (PAS dans le render) : un composant défini
// dans le render est recréé à chaque rendu → ses enfants sont démontés/remontés à
// chaque frappe → perte de focus (ex. +/- sur les inputs date).
const Field = ({ label, children }) => (
	<div style={generalStyles.fieldRow}>
		<label style={generalStyles.label}>{label}</label>
		{children}
	</div>
)

// Type de date en bulles segmentées (patron /location/rechercher). GE-48 :
// 0 = date de la fiche, 1 = date « Travaux terminés », 2 = date d'une
// transaction de la fiche (les transactions sans date ne comptent pas).
const DATE_TYPES = [
	{ value: 0, label: "Création" },
	{ value: 1, label: "Travaux terminés" },
	{ value: 2, label: "Facturation" },
]

// Formulaire de recherche atelier (V2). Page 1/2 : la saisie des critères. Au
// clic « Rechercher », on mémorise les critères, on lance la recherche puis on
// navigue vers la page de résultats (`/atelier/resultats`).
const AtelierSearchV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { isBoss } = useGroups()
	const employes = useSelector((state) => state.masterReducer.employes)
	const fetchEmployesStatus = useSelector(
		(state) => state.masterReducer.fetchEmployes,
	)
	const { criteres, loading } = useSelector(
		(state) => state.atelierSearchInfo,
	)

	// Pré-remplissage depuis la dernière recherche (criteres stockés en
	// DD-MM-YYYY pour les dates → convertis en ISO pour les <input type="date">).
	const [form, setForm] = useState(() => ({
		nom: criteres?.nom || "",
		marque: criteres?.marque || "",
		plaques: criteres?.plaques || "",
		employe: criteres?.employe || 0,
		etat: criteres?.etat ?? -2,
		dateT: criteres?.dateT ?? 0,
		dateS: dateFrToISO(criteres?.dateS || ""),
		dateE: dateFrToISO(criteres?.dateE || ""),
		svp: criteres?.svp || false,
		boss: criteres?.boss || false,
	}))

	useEffect(() => {
		dispatch(fetchEmployes())
	}, [dispatch])

	const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))
	// Entrée sur « Du » → focus « Au » ; Entrée sur « Au » → lance la recherche.
	const refDateE = useRef(null)

	const reset = () =>
		setForm({
			nom: "",
			marque: "",
			plaques: "",
			employe: 0,
			etat: -2,
			dateT: 0,
			dateS: "",
			dateE: "",
			svp: false,
			boss: false,
		})

	const search = () => {
		// Le backend attend les dates en DD-MM-YYYY (validDate).
		const criteres = {
			nom: form.nom,
			marque: form.marque,
			plaques: form.plaques,
			employe: form.employe,
			etat: form.etat,
			dateT: form.dateT,
			dateS: dateISOToFr(form.dateS),
			dateE: dateISOToFr(form.dateE),
			svp: form.svp,
			boss: form.boss,
		}
		dispatch(setAtelierSearchCriteres(criteres))
		dispatch(fetchAtelierSearchV2(criteres)).then((json) => {
			if (json !== null) history.push("/atelier/resultats")
		})
	}

	const onEnter = (e) => {
		if (e.key === "Enter") search()
	}

	return (
		<div style={generalStyles.page}>
			<div style={{ maxWidth: 640, margin: "0 auto" }}>
				<div style={generalStyles.header}>
					<div>
						<div style={generalStyles.ficheNumber}>Atelier</div>
						<div style={generalStyles.title}>
							Rechercher une fiche
						</div>
					</div>
				</div>

				<div style={generalStyles.card}>
					<div style={generalStyles.fieldGroup}>
						<Field label="Nom">
							<input
								type="text"
								style={generalStyles.input}
								value={form.nom}
								onChange={(e) => set("nom", e.target.value)}
								onKeyDown={onEnter}
							/>
						</Field>
						<Field label="Marque">
							<input
								type="text"
								style={generalStyles.input}
								value={form.marque}
								onChange={(e) => set("marque", e.target.value)}
								onKeyDown={onEnter}
							/>
						</Field>
						<Field label="Plaques">
							<input
								type="text"
								style={{
									...generalStyles.input,
									textTransform: "uppercase",
								}}
								value={form.plaques}
								onChange={(e) => set("plaques", e.target.value)}
								onKeyDown={onEnter}
							/>
						</Field>
						<Field label="Mécanicien">
							{fetchEmployesStatus &&
							fetchEmployesStatus.loading ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								<select
									style={generalStyles.select}
									value={form.employe}
									onChange={(e) =>
										set("employe", parseInt(e.target.value))
									}
								>
									<option value={0}>-</option>
									{employes.map((dep) => (
										<option
											key={dep.id + dep.nom}
											value={dep.id}
										>
											{dep.nom}
										</option>
									))}
								</select>
							)}
						</Field>
						<Field label="État">
							<select
								style={generalStyles.select}
								value={form.etat}
								onChange={(e) =>
									set("etat", parseInt(e.target.value))
								}
							>
								<option value={-2}>-</option>
								{ATELIER_ETATS_CHOICES.map((etat, index) => (
									<option
										key={`etat_${index}`}
										value={etat[0]}
									>
										{etat[1]}
									</option>
								))}
							</select>
						</Field>
						<Field label="Date">
							<div
								style={{
									...generalStyles.segmented,
									marginBottom: 0,
								}}
							>
								{DATE_TYPES.map((opt) => {
									const active =
										Number(form.dateT) === opt.value
									return (
										<button
											key={opt.value}
											type="button"
											style={generalStyles.segment(
												active,
											)}
											onClick={() =>
												set("dateT", opt.value)
											}
										>
											{opt.label}
										</button>
									)
								})}
							</div>
						</Field>
						<Field label="Du">
							<InputV2
								type="date"
								style={generalStyles.input}
								value={form.dateS}
								onChange={(e) => set("dateS", e.target.value)}
								nextRef={refDateE}
							/>
						</Field>
						<Field label="Au">
							<InputV2
								ref={refDateE}
								type="date"
								style={generalStyles.input}
								value={form.dateE}
								onChange={(e) => set("dateE", e.target.value)}
								onEnter={search}
							/>
						</Field>
						<Field label="SVP">
							<input
								type="checkbox"
								style={{
									width: 18,
									height: 18,
									cursor: "pointer",
								}}
								checked={form.svp}
								onChange={() => set("svp", !form.svp)}
							/>
						</Field>
						{isBoss && (
							<Field label="Boss">
								<input
									type="checkbox"
									style={{
										width: 18,
										height: 18,
										cursor: "pointer",
									}}
									checked={form.boss}
									onChange={() => set("boss", !form.boss)}
								/>
							</Field>
						)}
					</div>
				</div>

				<div style={{ ...generalStyles.footer, marginTop: 20 }}>
					<button
						type="button"
						style={generalStyles.btnSecondary}
						onClick={reset}
					>
						Réinitialiser
					</button>
					<button
						type="button"
						style={generalStyles.btnPrimary}
						onClick={search}
						disabled={loading}
					>
						{loading ? (
							<FontAwesomeIcon icon={faSync} spin />
						) : (
							"Rechercher"
						)}
					</button>
				</div>

				<div
					style={{
						marginTop: 16,
						fontSize: 12,
						color: COLORS.textLight,
						textAlign: "center",
					}}
				>
					Entrée pour lancer la recherche
				</div>
			</div>
		</div>
	)
}

export default AtelierSearchV2
